import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

/**
 * Named constants for the endpoint protection scoring formula.
 * Centralised here so compositeScore and radarData always stay in sync.
 */
const ENDPOINT_SCORE_WEIGHT_HIGH   = 10;  // weight applied to high-severity alerts
const ENDPOINT_SCORE_WEIGHT_MEDIUM = 5;   // weight applied to medium-severity alerts
const ENDPOINT_SCORE_WEIGHT_LOW    = 1;   // weight applied to low-severity alerts
const ENDPOINT_SCORE_LOG_SCALE     = 25;  // log10 multiplier — controls how steeply the score falls

/**
 * calculateEndpointProtectionScore
 * Returns a 0–100 score using a log-scaled, severity-weighted formula.
 * Log scaling prevents large alert volumes from collapsing the score to 0 while
 * still meaningfully differentiating between severity levels.
 *
 * @param {number} openAlerts - Total open Defender alerts
 * @param {number} highSeverity - Number of high-severity open alerts
 * @param {number} mediumSeverity - Number of medium-severity open alerts
 * @returns {number} Score in the range [0, 100]
 */
const calculateEndpointProtectionScore = (openAlerts, highSeverity, mediumSeverity) => {
  const lowSeverity = Math.max(0, openAlerts - highSeverity - mediumSeverity);
  const weightedAlerts =
    (highSeverity   * ENDPOINT_SCORE_WEIGHT_HIGH)   +
    (mediumSeverity * ENDPOINT_SCORE_WEIGHT_MEDIUM) +
    (lowSeverity    * ENDPOINT_SCORE_WEIGHT_LOW);

  // When there are no alerts the environment is clean — return a perfect score.
  // Otherwise apply log10 scaling so large volumes don't drive the score to 0.
  return weightedAlerts === 0
    ? 100
    : Math.max(0, 100 - Math.log10(weightedAlerts + 1) * ENDPOINT_SCORE_LOG_SCALE);
};

/**
 * ComprehensiveSecurityDashboard
 * Unified security dashboard combining all security domains:
 * - Endpoint Protection (Defender)
 * - Threat Hunting (DLP, Insider Risk)
 * - Network Security (Firewall, threats)
 * - Repository Security (GHAS, CodeQL, Dependabot)
 * - Device Compliance
 */
const ComprehensiveSecurityDashboard = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // State for all security data
  const [defenderData, setDefenderData] = useState(null);
  const [threatHuntingData, setThreatHuntingData] = useState(null);
  const [networkSecurityData, setNetworkSecurityData] = useState(null);
  const [githubSecurityData, setGithubSecurityData] = useState(null);
  const [intuneData, setIntuneData] = useState(null);

  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color')?.trim() || '#F63040';

  // Compute previous month date range for the IT Report (e.g., if March, report on February)
  const { prevMonthStart, prevMonthEnd } = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0); // last day of previous month
    return {
      prevMonthStart: start.toISOString().substring(0, 10),
      prevMonthEnd: end.toISOString().substring(0, 10)
    };
  }, []);

  // Fetch all security data in parallel.
  //
  // The fetch is expressed as an inline async IIFE inside the effect (rather than a
  // useCallback invoked from the effect) so that the state updates it performs are not
  // treated as synchronous setState within the effect body. This mirrors the established
  // data-loading convention used by OverallHealthScore.jsx and satisfies the
  // react-hooks/set-state-in-effect rule, which flags setState called synchronously from
  // an effect (a pattern that can trigger cascading renders).
  //
  // An `ignore` guard prevents state updates after the component has unmounted or the
  // effect's dependencies change mid-flight, avoiding "set state on unmounted component"
  // races when the previous request is still in progress.
  useEffect(() => {
    let ignore = false;

    (async () => {
      setLoading(true);
      setError('');

      try {
        const [defender, threatHunting, networkSec, githubSec, intune] = await Promise.allSettled([
          apiFetch('/api/Defender/stats'),
          apiFetch('/api/ThreatHunting/summary?daysBack=30'),
          apiFetch(`/api/NetworkSecurity/security/aggregated?startDate=${prevMonthStart}&endDate=${prevMonthEnd}`),
          apiFetch('/api/Github/security-summary?owner=Capital-Land-Management-Services&repo=CapTrac-ReactCore'),
          apiFetch('/api/Intune/device-inventory-summary')
        ]);

        if (ignore) return;

        if (defender.status === 'fulfilled' && defender.value.ok) {
          const data = await defender.value.json().catch(() => null);
          if (!ignore && data) setDefenderData(data);
        } else {
          console.warn('Defender API failed:', defender.status === 'fulfilled' ? defender.value.status : defender.reason);
        }
        if (threatHunting.status === 'fulfilled' && threatHunting.value.ok) {
          const data = await threatHunting.value.json().catch(() => null);
          if (!ignore && data) setThreatHuntingData(data);
        } else {
          console.warn('Threat Hunting API failed:', threatHunting.status === 'fulfilled' ? threatHunting.value.status : threatHunting.reason);
        }
        if (networkSec.status === 'fulfilled' && networkSec.value.ok) {
          const data = await networkSec.value.json().catch(() => null);
          if (!ignore && data) setNetworkSecurityData(data);
        } else {
          console.warn('Network Security API failed:', networkSec.status === 'fulfilled' ? networkSec.value.status : networkSec.reason);
        }
        if (githubSec.status === 'fulfilled' && githubSec.value.ok) {
          const data = await githubSec.value.json().catch(() => null);
          if (!ignore && data) setGithubSecurityData(data);
        } else {
          console.warn('GitHub Security API failed:', githubSec.status === 'fulfilled' ? githubSec.value.status : githubSec.reason);
        }
        if (intune.status === 'fulfilled' && intune.value.ok) {
          const data = await intune.value.json().catch(() => null);
          if (!ignore && data) setIntuneData(data);
        } else {
          console.warn('Intune API failed:', intune.status === 'fulfilled' ? intune.value.status : intune.reason);
        }
      } catch (e) {
        Sentry.captureException(e);
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => { ignore = true; };
  }, [apiFetch, prevMonthStart, prevMonthEnd]);

  // Helper function to extract compliant devices from Intune data
  const getIntuneCompliantDevices = (data) => {
    if (!data) return { compliant: 0, total: 0 };
    const total = data.totalDevices || data.TotalDevices || 0;
    const complianceStates = data.complianceStates || data.ComplianceStates || {};
    // Look for "Compliant" key (case-insensitive)
    const compliant = complianceStates.Compliant || complianceStates.compliant || 0;
    return { compliant, total };
  };

  // Calculate composite security score
  const compositeScore = useMemo(() => {
    let totalScore = 0;
    let totalWeight = 0;

    // Endpoint Protection (35%) - based on alert severity
    if (defenderData) {
      const openAlerts = defenderData.openAlerts ?? 0;
      const highSeverity = defenderData.highSeverityAlerts ?? 0;
      const mediumSeverity = defenderData.mediumSeverityAlerts ?? 0;
      const endpointScore = calculateEndpointProtectionScore(openAlerts, highSeverity, mediumSeverity);
      totalScore += endpointScore * 0.35;
      totalWeight += 0.35;
    }

    // Threat Hunting (25%) - based on DLP and insider risk incidents
    // Uses logarithmic scaling to handle high incident volumes gracefully
    if (threatHuntingData) {
      const dlpIncidents = threatHuntingData.totalDLPIncidents ?? 0;
      const insiderRiskIncidents = threatHuntingData.totalInsiderRiskIncidents ?? 0;
      const totalIncidents = dlpIncidents + insiderRiskIncidents;
      // Log scaling: 0 incidents = 100, ~10 = 85, ~50 = 74, ~150 = 67, ~500 = 59
      const threatScore = totalIncidents === 0 ? 100 : Math.max(0, 100 - Math.log10(totalIncidents + 1) * 15);
      totalScore += threatScore * 0.25;
      totalWeight += 0.25;
    }

    // Network Security (20%) - based on active alerts and failed auth attempts
    // Blacklisted IPs are excluded — they represent successful defensive blocks, not active threats
    if (networkSecurityData) {
      const activeAlerts = networkSecurityData.totalActiveAlerts ?? 0;
      const failedAuth = networkSecurityData.failedAuthAttempts ?? 0;
      // Only count actual unresolved threats; blacklisted IPs are successful defenses
      const actualThreats = activeAlerts + failedAuth;
      const threatScore = actualThreats === 0 ? 100 : Math.max(0, 100 - Math.log10(actualThreats + 1) * 15);
      totalScore += threatScore * 0.20;
      totalWeight += 0.20;
    }

    // Device Compliance (15%)
    const intuneStats = getIntuneCompliantDevices(intuneData);
    if (intuneData && intuneStats.total > 0) {
      // Use previousMonthComplianceRate if available, otherwise calculate
      const complianceRate = intuneData.previousMonthComplianceRate ?? intuneData.PreviousMonthComplianceRate ?? 
        ((intuneStats.compliant / intuneStats.total) * 100);
      totalScore += complianceRate * 0.15;
      totalWeight += 0.15;
    }

    // Repository Security (5%) — considers resolution effort, not just open count
    if (githubSecurityData) {
      const codeScanning = githubSecurityData.codeScanning || githubSecurityData.CodeScanning;
      const dependabot = githubSecurityData.dependabot || githubSecurityData.Dependabot;
      const secretScanning = githubSecurityData.secretScanning || githubSecurityData.SecretScanning;
      const totalOpen = (codeScanning?.open ?? codeScanning?.Open ?? 0) + (dependabot?.open ?? dependabot?.Open ?? 0) + (secretScanning?.open ?? secretScanning?.Open ?? 0);
      const totalFixed = (codeScanning?.fixed ?? codeScanning?.Fixed ?? 0) + (dependabot?.fixed ?? dependabot?.Fixed ?? 0);
      const resolutionRate = (totalFixed + totalOpen) > 0 ? totalFixed / (totalFixed + totalOpen) : 0;
      // Logarithmic scaling for open alerts, plus resolution rate bonus
      let repoScore = totalOpen === 0 ? 100 : Math.max(0, 100 - Math.log10(totalOpen + 1) * 25);
      repoScore = Math.min(100, repoScore + resolutionRate * 15);
      totalScore += repoScore * 0.05;
      totalWeight += 0.05;
    }

    return totalWeight > 0 ? (totalScore / totalWeight) : 0;
  }, [defenderData, threatHuntingData, networkSecurityData, githubSecurityData, intuneData]);

  // Radar chart data for multi-dimensional security view
  const radarData = useMemo(() => {
    const data = [];
    
    if (defenderData) {
      const openAlerts = defenderData.openAlerts ?? 0;
      const highSeverity = defenderData.highSeverityAlerts ?? 0;
      const mediumSeverity = defenderData.mediumSeverityAlerts ?? 0;
      const endpointScore = calculateEndpointProtectionScore(openAlerts, highSeverity, mediumSeverity);
      data.push({ domain: 'Endpoint Protection', score: endpointScore });
    }
    if (threatHuntingData) {
      const dlpIncidents = threatHuntingData.totalDLPIncidents ?? 0;
      const insiderRiskIncidents = threatHuntingData.totalInsiderRiskIncidents ?? 0;
      const totalIncidents = dlpIncidents + insiderRiskIncidents;
      const threatScore = totalIncidents === 0 ? 100 : Math.max(0, 100 - Math.log10(totalIncidents + 1) * 15);
      data.push({ domain: 'Threat Hunting', score: threatScore });
    }
    if (networkSecurityData) {
      const activeAlerts = networkSecurityData.totalActiveAlerts ?? 0;
      const failedAuth = networkSecurityData.failedAuthAttempts ?? 0;
      // Blacklisted IPs are successful defenses, not active threats
      const actualThreats = activeAlerts + failedAuth;
      const threatScore = actualThreats === 0 ? 100 : Math.max(0, 100 - Math.log10(actualThreats + 1) * 15);
      data.push({ domain: 'Network Security', score: threatScore });
    }
    const intuneStats = getIntuneCompliantDevices(intuneData);
    if (intuneData && intuneStats.total > 0) {
      const complianceRate = intuneData.previousMonthComplianceRate ?? intuneData.PreviousMonthComplianceRate ?? 
        ((intuneStats.compliant / intuneStats.total) * 100);
      data.push({ domain: 'Device Compliance', score: complianceRate });
    }
    if (githubSecurityData) {
      const codeScanning = githubSecurityData.codeScanning || githubSecurityData.CodeScanning;
      const dependabot = githubSecurityData.dependabot || githubSecurityData.Dependabot;
      const secretScanning = githubSecurityData.secretScanning || githubSecurityData.SecretScanning;
      const totalOpen = (codeScanning?.open ?? codeScanning?.Open ?? 0) + (dependabot?.open ?? dependabot?.Open ?? 0) + (secretScanning?.open ?? secretScanning?.Open ?? 0);
      const totalFixed = (codeScanning?.fixed ?? codeScanning?.Fixed ?? 0) + (dependabot?.fixed ?? dependabot?.Fixed ?? 0);
      const resolutionRate = (totalFixed + totalOpen) > 0 ? totalFixed / (totalFixed + totalOpen) : 0;
      let repoScore = totalOpen === 0 ? 100 : Math.max(0, 100 - Math.log10(totalOpen + 1) * 25);
      repoScore = Math.min(100, repoScore + resolutionRate * 15);
      data.push({ domain: 'Repository Security', score: repoScore });
    }

    return data;
  }, [defenderData, threatHuntingData, networkSecurityData, githubSecurityData, intuneData]);

  // LLM proxy for AI insights
  const intuneStatsForProxy = getIntuneCompliantDevices(intuneData);
  const llmProxyText = `Composite Security Score: ${compositeScore.toFixed(1)}%\n` +
    `Endpoint Protection: ${defenderData?.openAlerts ?? 0} open alerts (${defenderData?.highSeverityAlerts ?? 0} high, ${defenderData?.mediumSeverityAlerts ?? 0} medium)\n` +
    `Threat Hunting: ${(threatHuntingData?.totalDLPIncidents ?? 0) + (threatHuntingData?.totalInsiderRiskIncidents ?? 0)} incidents\n` +
    `Network Security (${networkSecurityData?.periodLabel ?? 'previous month'}): ${(networkSecurityData?.totalActiveAlerts ?? 0)} alerts, ${(networkSecurityData?.totalBlacklistedIPs ?? 0)} blacklisted IPs\n` +
    `Device Compliance: ${intuneStatsForProxy.compliant}/${intuneStatsForProxy.total}\n` +
    `Repository Vulnerabilities: ${((githubSecurityData?.codeScanning?.open ?? githubSecurityData?.CodeScanning?.Open) || 0) + ((githubSecurityData?.dependabot?.open ?? githubSecurityData?.Dependabot?.Open) || 0)}`;

  if (loading) return <HexagonLoader />;
  if (error) return <div style={{ color: 'red', padding: 16 }}>{error}</div>;

  const scoreColor = compositeScore >= 80 ? '#2e7d32' : compositeScore >= 60 ? '#ff9800' : '#d32f2f';
  const getScoreIcon = (score) => {
    if (score >= 80) return '✓';
    if (score >= 60) return '⚠';
    return '✗';
  };
  const getScoreText = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Critical';
  };

  return (
    <div className="dashboard-card group-wrapper comprehensive-security-dashboard">
      <div data-llm-proxy style={{ display: 'none' }}>{llmProxyText}</div>
      
      <h2 style={{ marginTop: 0, marginBottom: 16 }}>Enterprise Security Dashboard</h2>

      {/* Composite Security Score Banner */}
      <div className="dashboard-card exec-banner" style={{ 
        background: `linear-gradient(135deg, ${scoreColor}15 0%, ${scoreColor}30 100%)`, 
        borderLeft: `6px solid ${scoreColor}`
      }}>
        <div className="exec-banner-layout">
          <div className="exec-banner-score">
            <div className="exec-banner-value" style={{ color: scoreColor }}>
              {Math.round(compositeScore)}
              <span className="exec-banner-suffix">/ 100</span>
            </div>
            <div className="exec-banner-label" style={{ color: scoreColor }}>{getScoreIcon(compositeScore)} {getScoreText(compositeScore)}</div>
            <div className="exec-banner-sublabel">Composite Security Score</div>
          </div>
          <div className="exec-banner-body">
            <div className="exec-banner-text" style={{ color: '#333', lineHeight: 1.7 }}>
            <strong>Enterprise-wide security posture</strong> across endpoint protection, threat hunting, network defense, device compliance, and repository security.
            {compositeScore >= 80 && <span style={{ color: '#2e7d32', fontWeight: 600 }}> Excellent security health.</span>}
            {compositeScore >= 60 && compositeScore < 80 && <span style={{ color: '#ff9800', fontWeight: 600 }}> Moderate risk - attention needed.</span>}
            {compositeScore < 60 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> High risk - immediate action required.</span>}
          </div>
        </div>
        </div>
      </div>

      {/* Data Availability Notice */}
      {(!defenderData || !threatHuntingData || !networkSecurityData || !intuneData || !githubSecurityData) && (
        <div className="dashboard-card" style={{ 
          margin: '0 0 20px 0', 
          padding: 16, 
          background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)', 
          borderLeft: '4px solid #ffa000'
        }}>
          <div style={{ fontSize: 13, lineHeight: 1.7, color: '#333' }}>
            <strong>⚠️ Limited Data Availability:</strong> Some security data sources are currently unavailable. 
            Sections showing "N/A" indicate that the corresponding API endpoint is not responding or not configured. 
            The composite score is calculated only from available data sources. For complete security visibility, ensure all monitoring systems are operational.
          </div>
        </div>
      )}

      {/* Overview Section */}
      <div>
          {/* Multi-Dimensional Radar Chart */}
          <div className="dashboard-card radar-card" style={{ padding: 16, marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: 15 }}>Multi-Dimensional Security Posture</h4>
            <ResponsiveContainer width={isPrintMode ? 680 : "100%"} height={isPrintMode ? 280 : 300}>
              <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                <PolarGrid />
                <PolarAngleAxis dataKey="domain" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Radar name="Security Score" dataKey="score" stroke={primaryColor} fill={primaryColor} fillOpacity={0.6} isAnimationActive={!isPrintMode} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Key Metrics Grid */}
          <div className="security-grid">
            {/* Endpoint Protection */}
            {defenderData ? (() => {
              const openAlerts = defenderData.openAlerts ?? 0;
              const highSeverity = defenderData.highSeverityAlerts ?? 0;
              return (
                <div className="dashboard-card">
                  <div className="metric-label">Endpoint Protection</div>
                  <div className="metric-value" style={{ color: openAlerts === 0 ? '#2e7d32' : highSeverity > 0 ? '#d32f2f' : '#ff9800' }}>
                    {openAlerts}
                  </div>
                  <div className="metric-subtitle" style={{ marginTop: 4 }}>
                    Open Alerts ({highSeverity} High Severity)
                  </div>
                </div>
              );
            })() : (
              <div className="dashboard-card">
                <div className="metric-label">Endpoint Protection</div>
                <div className="metric-value--sm" style={{ fontWeight: 600, color: '#ff9800' }}>N/A</div>
                <div className="metric-subtitle" style={{ marginTop: 4 }}>Data unavailable</div>
              </div>
            )}

            {/* Device Compliance */}
            {(() => {
              const stats = getIntuneCompliantDevices(intuneData);
              const complianceRate = intuneData?.previousMonthComplianceRate ?? intuneData?.PreviousMonthComplianceRate ?? 
                (stats.total > 0 ? (stats.compliant / stats.total) * 100 : 0);
              return intuneData && stats.total > 0 ? (
                <div className="dashboard-card">
                  <div className="metric-label">Device Compliance</div>
                  <div className="metric-value--md" style={{ color: complianceRate >= 80 ? '#2e7d32' : '#ff9800' }}>
                    {complianceRate.toFixed(0)}%
                  </div>
                  <div className="metric-subtitle" style={{ marginTop: 4 }}>
                    {stats.compliant}/{stats.total} Compliant
                  </div>
                </div>
              ) : (
                <div className="dashboard-card unavailable-card">
                  <div className="metric-label">Device Compliance</div>
                  <div className="metric-value--sm" style={{ fontWeight: 600, color: '#ff9800' }}>N/A</div>
                  <div className="metric-subtitle" style={{ marginTop: 4 }}>Data unavailable</div>
                </div>
              );
            })()}

            {/* Threat Hunting */}
            {threatHuntingData ? (() => {
              const totalIncidents = (threatHuntingData.totalDLPIncidents ?? 0) + (threatHuntingData.totalInsiderRiskIncidents ?? 0);
              return (
                <div className="dashboard-card">
                  <div className="metric-label">Threat Hunting</div>
                  <div className="metric-value--md" style={{ color: totalIncidents === 0 ? '#2e7d32' : totalIncidents > 50 ? '#d32f2f' : '#ff9800' }}>
                    {totalIncidents}
                  </div>
                  <div className="metric-subtitle" style={{ marginTop: 4 }}>
                    {threatHuntingData.totalDLPIncidents ?? 0} DLP / {threatHuntingData.totalInsiderRiskIncidents ?? 0} Insider Risk
                  </div>
                </div>
              );
            })() : (
              <div className="dashboard-card unavailable-card">
                <div className="metric-label">Threat Hunting</div>
                <div className="metric-value--sm" style={{ fontWeight: 600, color: '#ff9800' }}>N/A</div>
                <div className="metric-subtitle" style={{ marginTop: 4 }}>Data unavailable</div>
              </div>
            )}

            {/* Network Security */}
            {networkSecurityData ? (() => {
              const activeAlerts = networkSecurityData.totalActiveAlerts ?? 0;
              const blacklistedIPs = networkSecurityData.totalBlacklistedIPs ?? 0;
              return (
                <div className="dashboard-card">
                  <div className="metric-label">Network Threats</div>
                  <div className="metric-value--md" style={{ color: activeAlerts === 0 ? '#2e7d32' : '#ff9800' }}>
                    {activeAlerts}
                  </div>
                  <div className="metric-subtitle" style={{ marginTop: 4 }}>
                    Active Alerts · {blacklistedIPs} Blacklisted IPs
                  </div>
                </div>
              );
            })() : (
              <div className="dashboard-card unavailable-card">
                <div className="metric-label">Network Threats</div>
                <div className="metric-value--sm" style={{ fontWeight: 600, color: '#ff9800' }}>N/A</div>
                <div className="metric-subtitle" style={{ marginTop: 4 }}>Data unavailable</div>
              </div>
            )}

            {/* Repository Security */}
            {githubSecurityData ? (
              <div className="dashboard-card">
                <div className="metric-label">Repository Security</div>
                <div className="metric-value--md" style={{ color: (githubSecurityData.codeScanning?.open || 0) + (githubSecurityData.dependabot?.open || 0) === 0 ? '#2e7d32' : '#ff9800' }}>
                  {(githubSecurityData.codeScanning?.open || 0) + (githubSecurityData.dependabot?.open || 0)}
                </div>
                <div className="metric-subtitle" style={{ marginTop: 4 }}>
                  Open Vulnerabilities
                </div>
              </div>
            ) : (
              <div className="dashboard-card unavailable-card">
                <div className="metric-label">Repository Security</div>
                <div className="metric-value--sm" style={{ fontWeight: 600, color: '#ff9800' }}>N/A</div>
                <div className="metric-subtitle" style={{ marginTop: 4 }}>Data unavailable</div>
              </div>
            )}
          </div>

          {/* Security Best Practices */}
          <div className="explanation-box" style={{ lineHeight: 1.7 }}>
            <strong style={{ fontSize: 13, color: '#333' }}>🎯 Security Health Summary:</strong>
            <ul>
              <li>Composite score calculated from 5 security domains with weighted importance</li>
              <li>All security domains are displayed below for comprehensive analysis</li>
              <li>Target: Maintain composite score above 80% for optimal security posture</li>
            </ul>
          </div>
        </div>

      {/* === ENDPOINT PROTECTION SECTION === */}
      {defenderData && (() => {
        const openAlerts = defenderData.openAlerts ?? 0;
        const highSeverity = defenderData.highSeverityAlerts ?? 0;
        const mediumSeverity = defenderData.mediumSeverityAlerts ?? 0;
        const lowSeverity = defenderData.lowSeverityAlerts ?? 0;
        return (
          <div style={{ marginTop: 32 }}>
            <h3 className="section-divider">
              🛡️ Endpoint Protection & Threat Detection
            </h3>
            
            <div className="security-grid">
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Open Alerts</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: openAlerts === 0 ? '#2e7d32' : highSeverity > 0 ? '#d32f2f' : '#ff9800' }}>
                  {openAlerts}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Requiring attention
                </div>
              </div>
              
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>High Severity</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: highSeverity === 0 ? '#2e7d32' : '#d32f2f' }}>
                  {highSeverity}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Critical priority
                </div>
              </div>
              
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Medium Severity</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: mediumSeverity === 0 ? '#2e7d32' : '#ff9800' }}>
                  {mediumSeverity}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Elevated risk
                </div>
              </div>
              
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Low Severity</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: '#2196f3' }}>
                  {lowSeverity}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Minor concerns
                </div>
              </div>
            </div>
            
            <div style={{ fontSize: 12, color: '#666', padding: 14, background: '#f5f5f5', borderRadius: 4, lineHeight: 1.7 }}>
              <strong>Endpoint Protection Summary:</strong> Microsoft Defender for Endpoint is tracking {openAlerts} open alerts. 
              Alert breakdown: {highSeverity} high severity, {mediumSeverity} medium severity, and {lowSeverity} low severity alerts requiring attention.
            </div>
          </div>
        );
      })()}

      {/* === THREAT HUNTING & DLP SECTION === */}
      {threatHuntingData && (() => {
        const dlpIncidents = threatHuntingData.totalDLPIncidents ?? 0;
        const insiderRiskIncidents = threatHuntingData.totalInsiderRiskIncidents ?? 0;
        const totalIncidents = dlpIncidents + insiderRiskIncidents;
        return (
          <div style={{ marginTop: 32 }}>
            <h3 className="section-divider">
              🔍 Advanced Threat Hunting & Data Protection
            </h3>
            
            <div className="security-grid">
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Total Incidents</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: totalIncidents > 50 ? '#d32f2f' : totalIncidents > 10 ? '#ff9800' : '#2e7d32' }}>
                  {totalIncidents}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Past 30 days
                </div>
              </div>
              
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>DLP Incidents</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: dlpIncidents === 0 ? '#2e7d32' : dlpIncidents > 50 ? '#d32f2f' : '#ff9800' }}>
                  {dlpIncidents}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Data loss prevention
                </div>
              </div>
              
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Insider Risk</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: insiderRiskIncidents === 0 ? '#2e7d32' : insiderRiskIncidents > 20 ? '#d32f2f' : '#ff9800' }}>
                  {insiderRiskIncidents}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Risk indicators
                </div>
              </div>
            </div>
            
            <div style={{ fontSize: 12, color: '#666', padding: 14, background: '#f5f5f5', borderRadius: 4, lineHeight: 1.7 }}>
              <strong>Threat Hunting Summary:</strong> Advanced threat hunting has detected {totalIncidents} total incidents in the past 30 days. 
              This includes {dlpIncidents} DLP-related incidents and {insiderRiskIncidents} insider risk indicators requiring review.
            </div>
          </div>
        );
      })()}

      {/* === NETWORK SECURITY SECTION === */}
      {networkSecurityData && (() => {
        const activeAlerts = networkSecurityData.totalActiveAlerts ?? 0;
        const blacklistedIPs = networkSecurityData.totalBlacklistedIPs ?? 0;
        const recentBlacklisted = networkSecurityData.recentBlacklistedIPs ?? 0;
        const packetLossEvents = networkSecurityData.totalHighPacketLossEvents ?? 0;
        const authAttempts = networkSecurityData.totalAuthAttempts ?? 0;
        const failedAuth = networkSecurityData.failedAuthAttempts ?? 0;
        const onlineRouters = networkSecurityData.activeRouters ?? 0;
        const totalRouters = networkSecurityData.totalRouters ?? 0;
        const offlineRouters = totalRouters - onlineRouters;
        return (
          <div style={{ marginTop: 32 }}>
            <h3 className="section-divider">
              🌐 Network Security & Router Monitoring
            </h3>

            <div className="security-grid">
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Active Alerts</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: activeAlerts === 0 ? '#2e7d32' : activeAlerts > 5 ? '#d32f2f' : '#ff9800' }}>
                  {activeAlerts}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Monitoring threshold breaches
                </div>
              </div>

              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Blacklisted IPs</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: blacklistedIPs === 0 ? '#2e7d32' : '#ff9800' }}>
                  {blacklistedIPs}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  {recentBlacklisted} detected recently
                </div>
              </div>

              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Packet Loss Events</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: packetLossEvents === 0 ? '#2e7d32' : packetLossEvents > 10 ? '#d32f2f' : '#ff9800' }}>
                  {packetLossEvents}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Hours with &gt;5% loss
                </div>
              </div>

              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Auth Attempts (RADIUS)</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: failedAuth === 0 ? '#2e7d32' : failedAuth > 10 ? '#d32f2f' : '#ff9800' }}>
                  {authAttempts}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  {failedAuth} failed attempts
                </div>
              </div>
            </div>

            {/* Router fleet status */}
            <div className="security-grid" style={{ marginTop: 12 }}>
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Router Fleet</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: '#2196f3' }}>
                  {totalRouters}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Managed routers
                </div>
              </div>

              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Online</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: '#2e7d32' }}>
                  {onlineRouters}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Responding to ICMP
                </div>
              </div>

              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Offline</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: offlineRouters === 0 ? '#2e7d32' : '#d32f2f' }}>
                  {offlineRouters}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Unreachable
                </div>
              </div>

              {networkSecurityData.highRiskRouters?.length > 0 && (
                <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>High Risk</div>
                  <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: '#d32f2f' }}>
                    {networkSecurityData.highRiskRouters.length}
                  </div>
                  <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                    Currently down
                  </div>
                </div>
              )}
            </div>

            {/* Alert breakdown table */}
            {networkSecurityData.alertBreakdown?.length > 0 && (
              <div className="dashboard-card" style={{ marginTop: 12, padding: 14 }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: 13 }}>Alert Breakdown</h4>
                <table className="data-table">
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ textAlign: 'left', padding: '6px', width: '100%' }}>Alert Type</th>
                      <th style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {networkSecurityData.alertBreakdown.map(ab => (
                      <tr key={ab.type} style={{ borderTop: '1px solid #eee' }}>
                        <td style={{ textAlign: 'left', padding: '6px', fontWeight: 600 }}>{ab.type}</td>
                        <td style={{ padding: '6px', textAlign: 'right' }}>{ab.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* High-risk router list */}
            {networkSecurityData.highRiskRouters?.length > 0 && (
              <div className="dashboard-card" style={{ marginTop: 12, padding: 14, borderLeft: '4px solid #d32f2f' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: '#d32f2f' }}>⚠ Unreachable Routers</h4>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.8 }}>
                  {networkSecurityData.highRiskRouters.map((name, i) => (
                    <li key={i} style={{ fontWeight: 600 }}>{name}</li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ fontSize: 12, color: '#666', padding: 14, background: '#f5f5f5', borderRadius: 4, lineHeight: 1.7, marginTop: 12 }}>
              <strong>Network Security Summary:</strong> Your router monitoring system is tracking {totalRouters} managed routers ({onlineRouters} online, {offlineRouters} offline). 
              {activeAlerts} active monitoring alerts, {blacklistedIPs} IPs on the centralized blacklist ({recentBlacklisted} detected recently), 
              and {packetLossEvents} hourly periods with significant packet loss ({'>'}5%).
              {' '}{authAttempts} RADIUS authentication attempts recorded ({failedAuth} failed).
              {networkSecurityData.highRiskRouters?.length > 0 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> {networkSecurityData.highRiskRouters.length} router{networkSecurityData.highRiskRouters.length > 1 ? 's' : ''} currently unreachable — investigate immediately.</span>}
            </div>
          </div>
        );
      })()}

      {/* === DEVICE COMPLIANCE SECTION === */}
      {intuneData && (() => {
        const stats = getIntuneCompliantDevices(intuneData);
        const totalDevices = intuneData.totalDevices ?? intuneData.TotalDevices ?? 0;
        const complianceRate = intuneData.previousMonthComplianceRate ?? intuneData.PreviousMonthComplianceRate ?? 
          (stats.total > 0 ? (stats.compliant / stats.total) * 100 : 0);
        const nonCompliant = stats.total - stats.compliant;
        return (
          <div style={{ marginTop: 32 }}>
            <h3 className="section-divider">
              📱 Device Compliance & Management
            </h3>
            
            <div className="security-grid">
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Compliance Rate</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: complianceRate >= 80 ? '#2e7d32' : '#ff9800' }}>
                  {complianceRate.toFixed(0)}%
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  {stats.compliant}/{stats.total} devices
                </div>
              </div>
              
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Compliant</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: '#2e7d32' }}>
                  {stats.compliant}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Meeting policies
                </div>
              </div>
              
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Non-Compliant</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: nonCompliant > 0 ? '#d32f2f' : '#2e7d32' }}>
                  {nonCompliant}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Requires action
                </div>
              </div>
              
              <div className="dashboard-card" style={{ padding: 14, margin: 0 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600 }}>Total Managed</div>
                <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6, color: '#2196f3' }}>
                  {totalDevices}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Under management
                </div>
              </div>
            </div>
            
            <div style={{ fontSize: 12, color: '#666', padding: 14, background: '#f5f5f5', borderRadius: 4, lineHeight: 1.7 }}>
              <strong>Device Compliance Summary:</strong> {stats.compliant} of {stats.total} managed devices 
              ({complianceRate.toFixed(0)}%) are currently compliant with organizational security policies. 
              {nonCompliant > 0 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> {nonCompliant} non-compliant devices require immediate attention.</span>}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ComprehensiveSecurityDashboard;
